import React from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { Icon, IconName } from 'components/ui';
import styles from './SidebarNavItem.module.scss';

export type SidebarStatusTone = 'neutral' | 'warning' | 'success' | 'brand';

export interface ISidebarNavItemProps {
  icon: React.ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
  active?: boolean;
  end?: boolean;
  trailing?: 'chevron' | 'expand' | 'collapse' | 'none';
  subtitle?: string;
  statusTone?: SidebarStatusTone;
}

function trailingIconName(
  trailing: ISidebarNavItemProps['trailing'],
): IconName | null {
  if (trailing === 'collapse') return 'chevron-down';
  if (trailing === 'expand' || trailing === 'chevron')
    return 'chevron-right';
  return null;
}

export default function SidebarNavItem({
  icon,
  label,
  to,
  onClick,
  active = false,
  end = true,
  trailing = 'chevron',
  subtitle,
  statusTone,
}: ISidebarNavItemProps) {
  const iconName = trailingIconName(trailing);
  const trailingNode = iconName ? (
    <Icon name={iconName} size={20} className={styles.chevron} />
  ) : null;

  const inner = (
    <>
      <span className={styles.tile}>{icon}</span>
      <span className={styles.labelBlock}>
        <span className={styles.label}>{label}</span>
        {subtitle ? (
          <span className={styles.subtitle}>
            {statusTone ? (
              <span
                className={clsx(styles.dot, styles[statusTone])}
                aria-hidden
              />
            ) : null}
            {subtitle}
          </span>
        ) : null}
      </span>
      {trailingNode}
    </>
  );

  if (to) {
    return (
      <NavLink
        to={to}
        end={end}
        title={label}
        onClick={onClick}
        className={({ isActive }) =>
          clsx(styles.item, (active || isActive) && styles.active)
        }
      >
        {inner}
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      className={clsx(styles.item, active && styles.active)}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}
